/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * A v2 record page laid out like Account 360: the record header, a row of
 * headline figures, tabs (kept in the address as ?tab=) over the record's own
 * sections, and a side column with the interaction timeline.
 *
 * Pages pass the pieces they already build (their actions, details body and
 * side-column cards) as tab content, so every handler, modal and permission
 * check stays where it is. Only where those pieces sit changes.
 */

import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { LayoutGrid } from "lucide-react";
import { v2 as t } from "styles/tokens/v2";
import Account360KpiStrip from "layouts/crm/account360/Account360KpiStrip";
import RecordHero, { BRAND_ACCENT } from "./RecordHero";

/** Stacks cards in a tab with the same gap as the side column. */
export function TabStack({ children }) {
  return <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>{children}</Box>;
}
TabStack.propTypes = { children: PropTypes.node };
TabStack.defaultProps = { children: null };

export default function RecordPageV2({ hero, tiles, tabs, aside, sectionsLabel }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const shown = tabs.filter(Boolean);
  const requested = searchParams.get("tab");
  const current = shown.some((tb) => tb.key === requested) ? requested : shown[0]?.key;
  const selectTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === shown[0]?.key) next.delete("tab");
    else next.set("tab", key);
    setSearchParams(next, { replace: true });
  };
  const active = shown.find((tb) => tb.key === current);
  const accent = BRAND_ACCENT;
  const tileList = tiles.filter(Boolean).map((tile) => (tile.tab ? { ...tile, onClick: () => selectTab(tile.tab) } : tile));

  return (
    <Box>
      <RecordHero {...hero} accent={accent} />

      <Account360KpiStrip tiles={tileList.slice(0, 6)} accent={accent} />

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          {shown.length > 1 && (
            <Card sx={{ p: 0, mb: 2.5, overflow: "hidden" }}>
              <Tabs
                value={current}
                onChange={(_e, key) => selectTab(key)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label={sectionsLabel}
                sx={{
                  px: 1,
                  minHeight: 48,
                  "& .MuiTabs-indicator": { bgcolor: accent.main, height: 3, borderRadius: "3px 3px 0 0" },
                  "& .MuiTab-root": { minHeight: 48, px: 1.5, gap: 0.75, color: t.text2, fontWeight: 500 },
                  "& .MuiTab-root.Mui-selected": { color: accent.ink, fontWeight: 600 },
                }}
              >
                {shown.map((tb) => {
                  const Icon = tb.icon || LayoutGrid;
                  return (
                    <Tab
                      key={tb.key}
                      value={tb.key}
                      iconPosition="start"
                      icon={<Icon size={16} aria-hidden="true" />}
                      label={
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                          {tb.label}
                          {typeof tb.count === "number" && (
                            <Box
                              component="span"
                              sx={{
                                minWidth: 20,
                                height: 20,
                                px: 0.6,
                                borderRadius: "10px",
                                fontSize: 11.5,
                                fontWeight: 700,
                                display: "inline-grid",
                                placeItems: "center",
                                bgcolor: current === tb.key ? accent.tint : t.surface2,
                                color: current === tb.key ? accent.ink : t.text2,
                              }}
                            >
                              {tb.count}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
            </Card>
          )}
          <Box role="tabpanel" aria-label={active?.label}>
            {active?.render(selectTab)}
          </Box>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box sx={{ position: { lg: "sticky" }, top: { lg: 16 }, display: "flex", flexDirection: "column", gap: 2.5 }}>
            {typeof aside === "function" ? aside(selectTab) : aside}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

RecordPageV2.propTypes = {
  // RecordHero's props, without the accent (always PearlCRM blue).
  hero: PropTypes.shape({
    icon: PropTypes.elementType.isRequired,
    eyebrow: PropTypes.node.isRequired,
    title: PropTypes.node,
    badges: PropTypes.node,
    meta: PropTypes.array,
    actions: PropTypes.node,
    label: PropTypes.string,
    animate: PropTypes.bool,
  }).isRequired,
  // Account360KpiStrip tiles; `tab` makes a tile open that tab.
  tiles: PropTypes.arrayOf(PropTypes.object),
  // [{ key, label, icon, count, render(selectTab) }]; the first is the default.
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
      count: PropTypes.number,
      render: PropTypes.func.isRequired,
    })
  ).isRequired,
  // Side column content, or a function of selectTab.
  aside: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  sectionsLabel: PropTypes.string,
};

RecordPageV2.defaultProps = { tiles: [], aside: null, sectionsLabel: "Record sections" };
